import React, { useState } from "react";
import { Card } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { useCustomErrors } from "go-form/hooks";
import { apiOrganization } from "../../../../../../../../services/Api/Organization/apiOrganization";
import {
	ApplicationApi,
	ApplicationProvider,
	ApplicationUpdateApi,
	ItemOrderType,
} from "../../../../../../../../services/Api/Organization/types";
import { AppState } from "../../utils/types";
import {
	convertTerminalsFormDataToDataAcceptableByBackend,
	getDefaultAppFormValues,
	getNumberOfSecondsFromAcceptanceTime,
	getRidOfReceivedTerminalApiKeysFromBackend,
	parseApplicationSettings,
} from "../../utils/utils";
import BetasiForm from "../Providers/Betasi/BetasiForm";
import CallRecorderForm from "../Providers/CallRecorder/CallRecorderForm";
import GoClientForm from "../Providers/GoClient/GoClientForm";
import GoCrmForm from "../Providers/GoCrm/GoCrmForm";
import GoHotelIntegrationForm from "../Providers/GoHotelIntegration/GoHotelIntegrationForm";
import GoOrderForm from "../Providers/GoOrder/GoOrderForm";
import GoZoneForm from "../Providers/GoZone/GoZoneForm";
import HardRockCafeForm from "../Providers/HardRockCafe/HardRockCafeForm";
import HolidayPmsForm from "../Providers/HolidayPms/HolidayPmsForm";
import GenericHotelForm from "../Providers/HotelAppForm/GenericHotelForm";
import HotelgramForm from "../Providers/Hotelgram/HotelgramForm";
import HotellionFormModalCard from "../Providers/Hotellion/HotellionFormModalCard";
import HotresForm from "../Providers/Hotres/HotresForm";
import InforStarlightForm from "../Providers/InforStarlight/InforStarlightForm";
import KWHotelForm from "../Providers/KWHotel/KWHotelForm";
import MewsForm from "../Providers/Mews/MewsForm";
import MultiLocationForm from "../Providers/MultiLocation/MultiLocationForm";
import NfHotelForm from "../Providers/NfHotel/NfHotelForm";
import WoltDriveForm from "../Providers/WoltDrive/WoltDriveForm";

interface Props {
	appState: AppState;
	handleUpdateApp: ((data: ApplicationApi) => void) | undefined;
	provider: ApplicationProvider;
	setAppState: React.Dispatch<React.SetStateAction<AppState | undefined>>;
	fetchApplication: () => Promise<void>;
}

const AppForm = ({ appState, handleUpdateApp, provider, setAppState, fetchApplication }: Props) => {
	const form = useForm<ApplicationUpdateApi>({
		criteriaMode: "all",
		defaultValues: getDefaultAppFormValues(appState, provider),
	});
	const { handleSubmit, reset, watch, setError, formState } = form;
	const [loading, setLoading] = useState<boolean>(false);
	const { addFlash, addSuccessFlash } = useFlash();
	const { t } = useTranslation();
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);
	const [submitFormTrigger, setSubmitFormTrigger] = useState(0);

	const processUpdateApp = (app: ApplicationApi) => {
		if (handleUpdateApp) {
			handleUpdateApp(app);
			setAppState({ ...appState, app });
		}
	};

	const getParsedFormData = (data: ApplicationUpdateApi): ApplicationUpdateApi => {
		const omitSettings = provider === ApplicationProvider.GOORDER ? ["menu_infos"] : undefined;
		const settings = parseApplicationSettings(data.settings, omitSettings);
		const newData = {
			name: data.name,
			id: appState.app.id,
			settings,
		};

		if (provider === ApplicationProvider.HOLIDAY_PMS) {
			const convertedTerminalsData = convertTerminalsFormDataToDataAcceptableByBackend(data.settings.terminals);
			newData.settings = getRidOfReceivedTerminalApiKeysFromBackend(newData.settings);
			newData.settings = { ...data.settings, ...convertedTerminalsData };
			delete newData.settings.terminals;
		}

		if (provider === ApplicationProvider.GOORDER) {
			const order_acceptance_time_seconds = getNumberOfSecondsFromAcceptanceTime(
				settings.number_of_time_units,
				settings.time_unit
			);

			delete settings.time_unit;
			delete settings.number_of_time_units;

			const { price_list_id } = settings;
			let priceListIdValue: string = "";

			if (Array.isArray(price_list_id)) {
				priceListIdValue = price_list_id.map((priceListId) => priceListId.id).join(",");
			} else if (typeof price_list_id === "string") {
				priceListIdValue = price_list_id;
			}

			let menuInfos: Record<string, any>[] = settings.menu_infos.filter(
				(menuInfo: Record<string, any>) =>
					menuInfo.menu_id || menuInfo.availability_id || menuInfo.order_types?.length > 0
			);

			const isOrderTypeIncluded = (orderType: ItemOrderType, orderTypes: ItemOrderType[]) =>
				(orderTypes || []).includes(orderType);

			menuInfos = menuInfos.map((menuInfo) => ({
				...menuInfo,
				pick_up: isOrderTypeIncluded(ItemOrderType.PICK_UP, menuInfo.order_types),
				delivery: isOrderTypeIncluded(ItemOrderType.DELIVERY, menuInfo.order_types),
				room_service: isOrderTypeIncluded(ItemOrderType.ROOM_SERVICE, menuInfo.order_types),
				dine_in: isOrderTypeIncluded(ItemOrderType.DINE_IN, menuInfo.order_types),
			}));
			menuInfos.forEach((menuInfo) => {
				delete menuInfo.order_types;
			});

			newData.settings = {
				...newData.settings,
				order_acceptance_time_seconds,
				price_list_id: priceListIdValue,
				menu_infos: JSON.stringify(menuInfos),
			};
		}

		return newData;
	};

	const onSubmit = handleSubmit(async (data) => {
		if (!validateCustomErrors()) return;

		setLoading(true);
		const params: Record<string, any> = { include: "settings" };
		const newData = getParsedFormData(data);

		try {
			const res = await apiOrganization.updateApp(newData, params);
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
			processUpdateApp(res);
			reset(watch());
			setSubmitFormTrigger((prev) => prev + 1);
		} catch (e) {
			handleError.form(e, setError, addFlash);
		}
		setLoading(false);
	});

	const renderForm = () => {
		switch (provider) {
			case ApplicationProvider.BETASI:
			case ApplicationProvider.BETASI_V2:
				return <BetasiForm provider={provider} form={form} setErrors={setErrors} appState={appState} />;
			case ApplicationProvider.GOHOTELINTEGRATION:
				return <GoHotelIntegrationForm form={form} appState={appState} />;
			case ApplicationProvider.CHART:
			case ApplicationProvider.M_HOTEL:
				return <GenericHotelForm form={form} appState={appState} />;
			case ApplicationProvider.GOZONE:
				return <GoZoneForm form={form} />;
			case ApplicationProvider.HARD_ROCK_CAFE:
				return <HardRockCafeForm form={form} setErrors={setErrors} />;
			case ApplicationProvider.NFHOTEL:
				return <NfHotelForm form={form} appState={appState} setErrors={setErrors} />;
			case ApplicationProvider.HOTRES:
				return <HotresForm form={form} appState={appState} />;
			case ApplicationProvider.HOTELGRAM:
				return <HotelgramForm form={form} appState={appState} />;
			case ApplicationProvider.HOLIDAY_PMS:
				return <HolidayPmsForm appState={appState} form={form} />;
			case ApplicationProvider.GOCLIENT:
				return <GoClientForm appState={appState} form={form} />;
			case ApplicationProvider.WOLT_DRIVE:
				return <WoltDriveForm appState={appState} form={form} />;
			case ApplicationProvider.INFOR_STARLIGHT:
				return <InforStarlightForm appState={appState} form={form} />;
			case ApplicationProvider.HOTELLION:
				return <HotellionFormModalCard appState={appState} form={form} />;
			case ApplicationProvider.MEWS:
				return <MewsForm appState={appState} form={form} />;
			case ApplicationProvider.CALL_RECORDER:
				return <CallRecorderForm form={form} />;
			case ApplicationProvider.GOCRM:
				return <GoCrmForm form={form} />;
			case ApplicationProvider.LOCATIONS:
				return <MultiLocationForm appState={appState} form={form} />;
			case ApplicationProvider.GOORDER:
				return (
					<GoOrderForm
						appState={appState}
						submitFormTrigger={submitFormTrigger}
						form={form}
						setErrors={setErrors}
						fetchApplication={fetchApplication}
					/>
				);
			case ApplicationProvider.KWHOTEL:
				return <KWHotelForm form={form} appState={appState} />;
			default:
				return <></>;
		}
	};

	return (
		<FormDirty formState={formState} onSubmit={onSubmit} className={"app-form"}>
			<Card>
				<Card.Body>{renderForm()}</Card.Body>
			</Card>
			<ButtonLoading loading={loading} onClick={onSubmit}>
				{t("common.action.save", { ns: "lib" })}
			</ButtonLoading>
		</FormDirty>
	);
};

export default AppForm;
